import { useState } from 'react'

import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormattedMessage, FormattedNumber } from 'react-intl'
import { styled } from 'styled-components'

import { CurrencyStringCents } from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'

import type { TaxComponentBreakdown } from '@/queries/canadianTaxComponentBreakdown.types'

import { Card, Row, Label, Amount } from '../taxUi'

/**
 * MaxTaxExposureDisplay — bid-confirmation maximum-tax view-model
 * render (T081 / FR-008 / data-model §4.2 / research.md R7 Reading B).
 *
 * The view-model is composed by the **listing service** at
 * `GET /v1/canadian-tax/listings/:listingId/max-exposure` (T075,
 * landed in svc/listing v16.6.0). This component is the buyer-facing
 * render of that DTO at bid-confirmation time.
 *
 * Reading B's third source (any prior max bid the buyer already has
 * in flight on this auction) can push `committedBidAmount` above the
 * current form values. When that happens, the buyer needs an explicit
 * affordance saying "this exposure is based on your existing max bid,
 * not the value you're about to submit" — without it, R7's UX-trust
 * rationale (buyers must not be surprised by exposure they didn't
 * just type) breaks down.
 *
 * Stub-implementation note: T079 / T080 ship the failing-test
 * scaffolds plus this component shell, matching the precedent set by
 * the listing repo's MR !880 (T071-T073) and the home-portal MR !1919
 * (T076-T077 running estimate). T081 (FP-1616) un-skips the tests and
 * wires it into the BidConfirmationModal, hooking up the
 * `useMaxTaxExposure(listingId, formSingleBid, formProxyMaxBid, buyerAddressId)`
 * custom hook (calls the listing service GET endpoint).
 */

export type MaxTaxExposureData = {
  committedBidAmount: number
  province: string
  applicableShippingRate: number
  buyerFee: number
  aggregatedRate: string
  maxTaxAmount: number
  // FP-2498 — per-tax-type breakdown for the expandable Total Tax
  // detail. Optional so pre-svc/listing-!907 clients still render the
  // scalar row unchanged.
  components?: TaxComponentBreakdown[]
}

type MaxTaxExposureDisplayProps = {
  data: MaxTaxExposureData
  /**
   * The buyer's form-side proxy-max input value (cents). When
   * `data.committedBidAmount > formProxyMaxBid`, Reading B's prior-bid
   * branch is active and the prior-bid affordance is shown.
   */
  formProxyMaxBid: number
  currencyCode: string
}

const MaxTaxExposureDisplay = ({
  data,
  formProxyMaxBid,
  currencyCode,
}: MaxTaxExposureDisplayProps) => {
  // `|| 0` guard so a malformed `aggregatedRate` from the listing
  // service renders 0% rather than `NaN%` — belt-and-suspenders
  // against contract drift (T075's MONEY_PATTERN guard already
  // protects the service-side path).
  const rate = Number.parseFloat(data.aggregatedRate) || 0

  // Reading B's prior-bid branch is active when the resolved
  // committed bid exceeds the proxy-max the buyer just typed.
  const isPriorBidActive = data.committedBidAmount > formProxyMaxBid

  return (
    <Card data-testid="max-tax-exposure">
      {isPriorBidActive && (
        <PriorBidAffordance data-testid="max-tax-exposure-prior-bid-affordance">
          <FormattedMessage
            id="BidConfirmationModal.maxTaxExposure.priorBidAffordance"
            values={{
              priorBid: (
                <CurrencyStringCents
                  value={data.committedBidAmount}
                  currency={currencyCode}
                />
              ),
            }}
          />
        </PriorBidAffordance>
      )}
      <Row>
        <Label>
          <FormattedMessage
            id="BidConfirmationModal.maxTaxExposure.label"
            values={{
              rate: (
                <FormattedNumber
                  value={rate}
                  style="percent"
                  // QC's QST rate (9.975%) needs 3 decimals so the
                  // visible rate never silently rounds to 9.98% on
                  // the bid-confirmation panel — matches the running
                  // estimate's precision choice (see RunningTaxEstimate).
                  maximumFractionDigits={3}
                />
              ),
            }}
          />
        </Label>
        <Amount data-testid="max-tax-exposure-amount">
          <CurrencyStringCents
            value={data.maxTaxAmount}
            currency={currencyCode}
          />
        </Amount>
      </Row>
      {data.components && data.components.length > 0 && (
        <TaxComponentsToggle
          components={data.components}
          currencyCode={currencyCode}
        />
      )}
    </Card>
  )
}

/**
 * FP-2498 — expandable per-tax-type breakdown for the bid-confirmation
 * modal. Every component + line renders even when amount is $0 per AC.
 */
const TaxComponentsToggle = ({
  components,
  currencyCode,
}: {
  components: TaxComponentBreakdown[]
  currencyCode: string
}) => {
  const [isOpen, setIsOpen] = useState(false)
  return (
    <BreakdownWrapper>
      <BreakdownToggle
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        data-testid="max-tax-exposure-breakdown-toggle"
      >
        <BreakdownToggleLabel>
          <FormattedMessage id="BidConfirmationModal.maxTaxExposure.breakdown.label" />
        </BreakdownToggleLabel>
        <ChevronIcon icon={isOpen ? faChevronUp : faChevronDown} />
      </BreakdownToggle>
      {isOpen && (
        <BreakdownList data-testid="max-tax-exposure-breakdown-list">
          {components.flatMap((c) =>
            c.lines.map((l) => (
              <BreakdownRow key={`${c.component}-${l.taxType}`}>
                <BreakdownRowLabel>
                  <FormattedMessage
                    id={`BidConfirmationModal.maxTaxExposure.breakdown.line.${c.component}.${l.taxType}`}
                  />
                </BreakdownRowLabel>
                <BreakdownRowValue>
                  <CurrencyStringCents
                    value={l.amount}
                    currency={currencyCode}
                  />
                </BreakdownRowValue>
              </BreakdownRow>
            ))
          )}
        </BreakdownList>
      )}
    </BreakdownWrapper>
  )
}

export default MaxTaxExposureDisplay

const PriorBidAffordance = styled.div`
  ${Typography.Body2}
  color: ${Colors.Semantic.Neutral.dark_grey};
  padding: 0.25rem 0;
`

const BreakdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.25rem;
`

const BreakdownToggle = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.25rem 0;
`

const BreakdownToggleLabel = styled.div`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
`

const ChevronIcon = styled(FontAwesomeIcon)`
  color: ${Colors.Semantic.Neutral.dark_grey};
  font-size: 0.75rem;
`

const BreakdownList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding-left: 0.75rem;
`

const BreakdownRow = styled.div`
  display: flex;
  justify-content: space-between;
`

const BreakdownRowLabel = styled.div`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
`

const BreakdownRowValue = styled.div`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
`
